import type { ReactNode } from 'react';
import { GripVertical } from 'lucide-react';
import clsx from 'clsx';
import type { TeamLiveTileDrag } from './TeamLiveSortableGrid';

export interface TeamLiveStrapProps {
  /** Primary line: driver name, or an external stream's label. */
  title: string;
  /** Secondary line, e.g. vehicle name. Omitted when there is nothing to say. */
  subtitle?: string | null;
  /**
   * Trailing metrics for the secondary line (elapsed time, latency). Rendered
   * after `subtitle` in the same monospace run.
   */
  metrics?: ReactNode;
  /** Accent bar colour, which doubles as the status indicator. */
  accentClassName: string;
  /** Status in words, announced to screen readers (the accent bar is visual only). */
  statusLabel?: string;
  /** Controls owned by the consumer (REC); the player appends mute/fullscreen. */
  children?: ReactNode;
  /**
   * Drag affordance from the sortable grid. The pointer listeners live on the
   * strap itself (see TeamLiveTile); this renders only the grip glyph, which
   * carries the accessible name and keyboard activation.
   */
  drag?: TeamLiveTileDrag | null;
}

/**
 * The Team Live tile strap: one always-visible bottom bar carrying everything
 * a tile needs to say — accent/status, identity, metrics — plus a slot for the
 * consumer's controls. It replaces the four independent corner overlays that
 * used to collide here (issue #738). Both member tiles and external streams
 * render it so the multiview reads as one system.
 */
export function TeamLiveStrap({
  title,
  subtitle,
  metrics,
  accentClassName,
  statusLabel,
  children,
  drag,
}: TeamLiveStrapProps) {
  const hasSubline = !!subtitle || !!metrics;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {drag ? (
        <button
          type="button"
          {...drag.gripProps}
          className="inline-flex h-6 w-4 shrink-0 cursor-grab items-center justify-center rounded text-white/35 transition-colors hover:text-white/80 active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      ) : null}

      <span
        aria-hidden
        className={clsx('h-6 w-[3px] shrink-0 rounded-full', accentClassName)}
      />

      <div className="min-w-0 leading-tight">
        <p className="truncate text-[13px] font-semibold text-white">{title}</p>
        {hasSubline ? (
          <p className="flex items-center gap-1.5 truncate font-mono text-[10px] text-white/55 [font-variant-numeric:tabular-nums]">
            {subtitle ? <span className="truncate">{subtitle}</span> : null}
            {subtitle && metrics ? (
              <span aria-hidden className="text-white/25">
                ·
              </span>
            ) : null}
            {metrics}
          </p>
        ) : null}
      </div>

      {children ? (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {children}
        </div>
      ) : null}

      {/* The accent bar carries status visually; announce it in text too. */}
      {statusLabel ? <span className="sr-only">{statusLabel}</span> : null}
    </div>
  );
}
